import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import PaginationControls, { paginateItems } from '../components/PaginationControls'
import { useApp } from '../context/AppContext'
import { isAprovacaoVisivelParaUsuario } from '../lib/aprovacaoVisibility'
import { formatCurrency } from '../utils'

function KPI({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <div className="card" style={{ padding: '16px 20px' }}>
      <p style={{ color: '#62666d', fontSize: 11, fontWeight: 500, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</p>
      <p style={{ color: color || '#f7f8f8', fontSize: 22, fontWeight: 600 }}>{value}</p>
      {sub && <p style={{ color: '#62666d', fontSize: 11, marginTop: 4 }}>{sub}</p>}
    </div>
  )
}

export default function Dashboard() {
  const {
    unidades,
    obras,
    itensOrcados,
    requisicoes,
    cotacoes,
    ordensCompra,
    itensOrdemCompra,
    contasPagar,
    pagamentos,
    aprovacoes,
    colaboradores,
    alocacoesColaboradores,
    currentUser,
  } = useApp()
  const navigate = useNavigate()
  const [unidadeId, setUnidadeId] = useState('')
  const [obraId, setObraId] = useState('')
  const [obrasPage, setObrasPage] = useState(1)
  const [obrasPageSize, setObrasPageSize] = useState(10)

  const obrasDaUnidade = unidadeId ? obras.filter(obra => obra.unidade_id === unidadeId) : obras
  const obrasFiltradas = obraId ? obrasDaUnidade.filter(obra => obra.id === obraId) : obrasDaUnidade
  const paginatedObras = paginateItems(obrasFiltradas, obrasPage, obrasPageSize)
  const unidadeSelecionada = unidades.find(unidade => unidade.id === unidadeId)
  const obraIds = new Set(obrasFiltradas.map(obra => obra.id))
  const itensFiltrados = itensOrcados.filter(item => obraIds.has(item.obra_id))
  const requisicoesFiltradas = requisicoes.filter(requisicao => obraIds.has(requisicao.obra_id))
  const requisicaoIds = new Set(requisicoesFiltradas.map(requisicao => requisicao.id))
  const cotacoesFiltradas = cotacoes.filter(cotacao => requisicaoIds.has(cotacao.requisicao_id))
  const cotacaoIds = new Set(cotacoesFiltradas.map(cotacao => cotacao.id))
  const ordensFiltradas = ordensCompra.filter(ordem => obraIds.has(ordem.obra_id))
  const ordemIds = new Set(ordensFiltradas.map(ordem => ordem.id))
  const ordensAtivas = ordensFiltradas.filter(ordem => ordem.status !== 'CANCELADA')
  const ordemAtivaIds = new Set(ordensAtivas.map(ordem => ordem.id))
  const itensOrdemFiltrados = itensOrdemCompra.filter(item => ordemAtivaIds.has(item.ordem_compra_id))
  const contasFiltradas = contasPagar.filter(conta => obraIds.has(conta.obra_id))
  const contaIds = new Set(contasFiltradas.map(conta => conta.id))
  const pagamentosFiltrados = pagamentos.filter(pagamento => contaIds.has(pagamento.conta_pagar_id))
  const pagamentoIds = new Set(pagamentosFiltrados.map(pagamento => pagamento.id))
  const alocacoesFiltradas = alocacoesColaboradores.filter(alocacao => obraIds.has(alocacao.obra_id))
  const colaboradorIdsAlocados = new Set(alocacoesFiltradas.map(alocacao => alocacao.colaborador_id))
  const contasEmAberto = contasFiltradas.filter(conta => !['PAGO', 'CANCELADO'].includes(conta.status))

  const aprovacoesFiltradas = aprovacoes.filter(aprovacao => {
    if (!isAprovacaoVisivelParaUsuario(aprovacao, currentUser)) return false
    if (!unidadeId) return true
    if (aprovacao.entidade_tipo === 'REQUISICAO') return requisicaoIds.has(aprovacao.entidade_id)
    if (aprovacao.entidade_tipo === 'COTACAO') return cotacaoIds.has(aprovacao.entidade_id)
    if (aprovacao.entidade_tipo === 'ORDEM_COMPRA') return ordemIds.has(aprovacao.entidade_id)
    if (aprovacao.entidade_tipo === 'CONTA_PAGAR') return contaIds.has(aprovacao.entidade_id)
    if (aprovacao.entidade_tipo === 'PAGAMENTO') return pagamentoIds.has(aprovacao.entidade_id)
    return false
  })

  const totalOrcado = itensFiltrados
    .filter(item => item.status !== 'CANCELADO')
    .reduce((s, item) => s + Number(item.valor_total_previsto || 0), 0)
  const totalComprometidoOC = ordensAtivas.reduce((s, ordem) => s + Number(ordem.valor_total || 0), 0)
  const totalComprometidoItensOC = itensOrdemFiltrados.reduce((s, item) => s + Number(item.valor_total || 0), 0)
  const totalComprometido = Math.max(totalComprometidoOC, totalComprometidoItensOC)
  const totalAPagar = contasEmAberto.reduce((s, conta) => s + Math.max(0, conta.valor_previsto - conta.valor_pago), 0)
  const totalPagoFinanceiro = contasFiltradas.reduce((s, conta) => s + conta.valor_pago, 0)
  const totalPagoRegistrado = pagamentosFiltrados.reduce((s, pagamento) => s + Number(pagamento.valor_pago || 0), 0)
  const totalPago = Math.max(totalPagoRegistrado, totalPagoFinanceiro)
  const totalSaldo = totalOrcado - totalComprometido
  const totalMaoObra = alocacoesFiltradas.reduce((s, a) => s + a.valor_alocado, 0)

  const pendentes = aprovacoesFiltradas.filter(a => a.status === 'PENDENTE').length
  const atrasadas = contasFiltradas.filter(c => c.status === 'ATRASADO').length
  const acimaSaldo = itensFiltrados.filter(i => i.saldo_atual < 0).length

  const hoje = new Date()
  const proxSemana = new Date(hoje)
  proxSemana.setDate(hoje.getDate() + 7)
  const contasSemana = contasFiltradas.filter(c => {
    const v = new Date(c.data_vencimento)
    return v >= hoje && v <= proxSemana && c.status !== 'PAGO' && c.status !== 'CANCELADO'
  })

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ color: '#f7f8f8', fontSize: 18, fontWeight: 600 }}>Dashboard Executivo</h1>
        <p style={{ color: '#62666d', fontSize: 13, marginTop: 2 }}>
          {obrasFiltradas.length} obra{obrasFiltradas.length !== 1 ? 's' : ''} ativa{obrasFiltradas.length !== 1 ? 's' : ''} - {unidadeSelecionada ? `Unidade ${unidadeSelecionada.nome}` : 'Todas as unidades'}
        </p>
        <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <label htmlFor="dashboard-unidade" style={{ color: '#8a8f98', fontSize: 12 }}>Unidade</label>
          <select
            id="dashboard-unidade"
            value={unidadeId}
            onChange={event => {
              setUnidadeId(event.target.value)
              setObraId('')
              setObrasPage(1)
            }}
            style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, color: '#f7f8f8', fontSize: 13, padding: '7px 10px', minWidth: 180, outline: 'none' }}
          >
            <option value="">Todas as unidades</option>
            {unidades.map(unidade => (
              <option key={unidade.id} value={unidade.id}>{unidade.nome}</option>
            ))}
          </select>
          <label htmlFor="dashboard-obra" style={{ color: '#8a8f98', fontSize: 12 }}>Obra</label>
          <select
            id="dashboard-obra"
            value={obraId}
            onChange={event => { setObraId(event.target.value); setObrasPage(1) }}
            style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, color: '#f7f8f8', fontSize: 13, padding: '7px 10px', minWidth: 220, outline: 'none' }}
          >
            <option value="">Todas as obras</option>
            {obrasDaUnidade.map(obra => (
              <option key={obra.id} value={obra.id}>{obra.nome_obra}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
        <KPI label="Orcado" value={formatCurrency(totalOrcado)} />
        <KPI label="Comprometido" value={formatCurrency(totalComprometido)} sub={`A pagar: ${formatCurrency(totalAPagar)}`} color="#fbbf24" />
        <KPI label="Pago / Realizado" value={formatCurrency(totalPago)} color="#34d399" />
        <KPI label="Mao de Obra" value={formatCurrency(totalMaoObra)} color="#a78bfa" />
        <KPI label="Saldo Orcamentario" value={formatCurrency(totalSaldo)} color={totalSaldo < 0 ? '#f87171' : '#60a5fa'} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="card" style={{ padding: '16px 20px' }}>
          <p style={{ color: '#8a8f98', fontSize: 12, fontWeight: 500, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Alertas</p>
          <div className="flex flex-col gap-2">
            {pendentes > 0 && (
              <button onClick={() => navigate('/aprovacoes')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 6, padding: '8px 12px', cursor: 'pointer', textAlign: 'left' }}>
                <span style={{ color: '#fbbf24', fontSize: 13 }}>Aprovacoes pendentes</span>
                <span style={{ color: '#fbbf24', fontSize: 13, fontWeight: 600 }}>{pendentes}</span>
              </button>
            )}
            {atrasadas > 0 && (
              <button onClick={() => navigate('/contas-pagar')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, padding: '8px 12px', cursor: 'pointer', textAlign: 'left' }}>
                <span style={{ color: '#f87171', fontSize: 13 }}>Contas atrasadas</span>
                <span style={{ color: '#f87171', fontSize: 13, fontWeight: 600 }}>{atrasadas}</span>
              </button>
            )}
            {acimaSaldo > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, padding: '8px 12px' }}>
                <span style={{ color: '#f87171', fontSize: 13 }}>Itens acima do orcamento</span>
                <span style={{ color: '#f87171', fontSize: 13, fontWeight: 600 }}>{acimaSaldo}</span>
              </div>
            )}
            {pendentes === 0 && atrasadas === 0 && acimaSaldo === 0 && (
              <p style={{ color: '#62666d', fontSize: 13 }}>Nenhum alerta no momento.</p>
            )}
          </div>
        </div>

        <div className="card" style={{ padding: '16px 20px' }}>
          <p style={{ color: '#8a8f98', fontSize: 12, fontWeight: 500, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Contas a Pagar - Proximos 7 dias</p>
          {contasSemana.length === 0 ? (
            <p style={{ color: '#62666d', fontSize: 13 }}>Nenhuma conta nos proximos 7 dias.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {contasSemana.map(c => (
                <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <div>
                    <p style={{ color: '#d0d6e0', fontSize: 13 }}>{c.descricao}</p>
                    <p style={{ color: '#62666d', fontSize: 11 }}>Venc. {new Date(c.data_vencimento).toLocaleDateString('pt-BR')}</p>
                  </div>
                  <span style={{ color: '#fbbf24', fontSize: 13, fontWeight: 500 }}>{formatCurrency(c.valor_previsto - c.valor_pago)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ padding: '16px 20px', marginBottom: 16 }}>
        <p style={{ color: '#8a8f98', fontSize: 12, fontWeight: 500, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Pipeline de Suprimentos</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
          {[
            { label: 'Requisicoes', count: requisicoesFiltradas.length, path: '/requisicoes', color: '#60a5fa' },
            { label: 'Cotacoes', count: cotacoesFiltradas.length, path: '/cotacoes', color: '#a78bfa' },
            { label: 'Ordens Compra', count: ordensFiltradas.length, path: '/ordens-compra', color: '#fbbf24' },
            { label: 'Contas a Pagar', count: contasFiltradas.length, path: '/contas-pagar', color: '#f87171' },
            { label: 'Colaboradores', count: unidadeId ? colaboradorIdsAlocados.size : colaboradores.length, path: '/colaboradores', color: '#34d399' },
          ].map(item => (
            <button key={item.path} onClick={() => navigate(item.path)} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, padding: '12px', cursor: 'pointer', textAlign: 'center' }}>
              <p style={{ color: item.color, fontSize: 24, fontWeight: 700 }}>{item.count}</p>
              <p style={{ color: '#8a8f98', fontSize: 12, marginTop: 4 }}>{item.label}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="card" style={{ padding: '16px 20px' }}>
        <p style={{ color: '#8a8f98', fontSize: 12, fontWeight: 500, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Obras em Andamento</p>
        {obrasFiltradas.length === 0 ? (
          <p style={{ color: '#62666d', fontSize: 13 }}>Nenhuma obra cadastrada.</p>
        ) : (
          <>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                {['Obra', 'Status', 'Previsto', 'A Pagar', 'Pago', 'Saldo'].map(h => (
                  <th key={h} style={{ color: '#62666d', fontSize: 11, fontWeight: 500, padding: '0 0 8px', textAlign: 'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedObras.items.map(obra => {
                const itensObra = itensFiltrados.filter(item => item.obra_id === obra.id)
                const contas = contasFiltradas.filter(conta => conta.obra_id === obra.id)
                const ordensObra = ordensAtivas.filter(ordem => ordem.obra_id === obra.id)
                const ordemObraIds = new Set(ordensObra.map(ordem => ordem.id))
                const itensOCObra = itensOrdemFiltrados.filter(item => ordemObraIds.has(item.ordem_compra_id))
                const previsto = itensObra.filter(item => item.status !== 'CANCELADO').reduce((s, item) => s + Number(item.valor_total_previsto || 0), 0)
                const comprometidoOC = ordensObra.reduce((s, ordem) => s + Number(ordem.valor_total || 0), 0)
                const comprometidoItens = itensOCObra.reduce((s, item) => s + Number(item.valor_total || 0), 0)
                const comprometido = Math.max(comprometidoOC, comprometidoItens)
                const pagoFinanceiro = contas.reduce((s, conta) => s + conta.valor_pago, 0)
                const contaObraIds = new Set(contas.map(conta => conta.id))
                const pagoRegistrado = pagamentosFiltrados.filter(pagamento => contaObraIds.has(pagamento.conta_pagar_id)).reduce((s, pagamento) => s + Number(pagamento.valor_pago || 0), 0)
                const pago = Math.max(pagoRegistrado, pagoFinanceiro)
                const aPagar = contas
                  .filter(conta => !['PAGO', 'CANCELADO'].includes(conta.status))
                  .reduce((s, conta) => s + Math.max(0, conta.valor_previsto - conta.valor_pago), 0) || Math.max(0, comprometido - pago)
                const saldo = previsto - comprometido
                return (
                  <tr key={obra.id} className="table-row-hover" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding: '10px 0', color: '#d0d6e0', fontSize: 13 }}>{obra.nome_obra}</td>
                    <td style={{ padding: '10px 0' }}><span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, background: 'rgba(52,211,153,0.12)', color: '#34d399' }}>{obra.status.replace('_', ' ')}</span></td>
                    <td style={{ padding: '10px 0', color: '#d0d6e0', fontSize: 13 }}>{formatCurrency(previsto)}</td>
                    <td style={{ padding: '10px 0', color: '#fbbf24', fontSize: 13 }}>{formatCurrency(aPagar)}</td>
                    <td style={{ padding: '10px 0', color: '#34d399', fontSize: 13 }}>{formatCurrency(pago)}</td>
                    <td style={{ padding: '10px 0', color: '#60a5fa', fontSize: 13, fontWeight: 500 }}>{formatCurrency(saldo)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <PaginationControls
            page={paginatedObras.safePage}
            pageSize={obrasPageSize}
            totalItems={obrasFiltradas.length}
            onPageChange={setObrasPage}
            onPageSizeChange={setObrasPageSize}
          />
        </>
          )}
      </div>
    </div>
  )
}

